import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { assistantAPI } from '../lib/api';
import { MessageCircle, Search, Download, ChevronDown, ChevronRight, ThumbsUp, ThumbsDown, Copy, Loader2 } from 'lucide-react';

function renderInline(text) {
  var parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map(function renderPart(p, i) {
    if (p.length > 4 && p.slice(0, 2) === '**' && p.slice(-2) === '**') {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function renderMarkdown(text) {
  var lines = String(text || '').split('\n');
  var blocks = [];
  var i = 0;
  while (i < lines.length) {
    var raw = lines[i];
    var trimmed = raw.replace(/^\s+/, '');
    var hmatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (hmatch) {
      blocks.push({ type: 'h', level: hmatch[1].length, text: hmatch[2] });
      i = i + 1;
      continue;
    }
    if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      blocks.push({ type: 'hr' });
      i = i + 1;
      continue;
    }
    if (trimmed.slice(0, 2) === '- ' || trimmed.slice(0, 2) === '• ') {
      var bitems = [];
      while (i < lines.length) {
        var bt = lines[i].replace(/^\s+/, '');
        if (bt.slice(0, 2) !== '- ' && bt.slice(0, 2) !== '• ') { break; }
        bitems.push(bt.slice(2));
        i = i + 1;
      }
      blocks.push({ type: 'ul', items: bitems });
      continue;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      var nitems = [];
      while (i < lines.length) {
        var nt = lines[i].replace(/^\s+/, '');
        if (!/^\d+\.\s/.test(nt)) { break; }
        nitems.push(nt.replace(/^\d+\.\s/, ''));
        i = i + 1;
      }
      blocks.push({ type: 'ol', items: nitems });
      continue;
    }
    if (trimmed.slice(0, 2) === '> ' || trimmed === '>') {
      var qitems = [];
      while (i < lines.length) {
        var qt = lines[i].replace(/^\s+/, '');
        if (qt.slice(0, 2) !== '> ' && qt !== '>') { break; }
        qitems.push(qt.replace(/^>\s?/, ''));
        i = i + 1;
      }
      blocks.push({ type: 'quote', items: qitems });
      continue;
    }
    if (trimmed === '') {
      blocks.push({ type: 'space' });
      i = i + 1;
      continue;
    }
    blocks.push({ type: 'p', text: raw });
    i = i + 1;
  }

  return blocks.map(function renderBlock(b, bi) {
    if (b.type === 'ul') {
      return (
        <ul key={bi} className="list-disc pl-5 my-1 space-y-0.5">
          {b.items.map(function renderBLi(it, ii) { return <li key={ii}>{renderInline(it)}</li>; })}
        </ul>
      );
    }
    if (b.type === 'ol') {
      return (
        <ol key={bi} className="list-decimal pl-5 my-1 space-y-0.5">
          {b.items.map(function renderNLi(it, ii) { return <li key={ii}>{renderInline(it)}</li>; })}
        </ol>
      );
    }
    if (b.type === 'quote') {
      return (
        <blockquote key={bi} className="border-l-2 border-slate-300 pl-3 my-1 text-slate-600 italic">
          {b.items.map(function renderQ(it, ii) { return <div key={ii}>{renderInline(it)}</div>; })}
        </blockquote>
      );
    }
    if (b.type === 'h') {
      var hcls = b.level === 1
        ? 'text-sm font-bold text-slate-800 mt-2 mb-1'
        : (b.level === 2
            ? 'text-[13px] font-semibold text-slate-700 mt-2 mb-0.5'
            : 'text-xs font-semibold text-slate-600 mt-1.5 mb-0.5');
      return <div key={bi} className={hcls}>{renderInline(b.text)}</div>;
    }
    if (b.type === 'hr') {
      return <hr key={bi} className="my-2 border-slate-200" />;
    }
    if (b.type === 'space') {
      return <div key={bi} className="h-2" />;
    }
    return <p key={bi} className="my-1 leading-relaxed">{renderInline(b.text)}</p>;
  });
}

function TurnRow({ t }) {
  return (
    <div className="border-b border-slate-100 py-2 last:border-0">
      <div className="text-sm font-medium text-slate-800">{t.q}</div>
      <div className="text-sm text-slate-600 mt-0.5">{renderMarkdown(t.a)}</div>
      <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-slate-400">
        <span>{(t.at || '').replace('T', ' ').slice(0, 16)}</span>
        {t.rating === 'up' ? (
          <span className="inline-flex items-center gap-1 text-green-600"><ThumbsUp className="h-3 w-3" />gut</span>
        ) : null}
        {t.rating === 'down' ? (
          <span className="inline-flex items-center gap-1 text-red-600"><ThumbsDown className="h-3 w-3" />schlecht</span>
        ) : null}
        {t.copied > 0 ? (
          <span className="inline-flex items-center gap-1"><Copy className="h-3 w-3" />{t.copied}x kopiert</span>
        ) : null}
        {t.clicks && t.clicks.length > 0 ? (
          <span>{t.clicks.length} Klick(s): {t.clicks.map(function mapClick(c) { return c.target; }).join(', ')}</span>
        ) : null}
      </div>
    </div>
  );
}

export function AssistantChatLogs() {
  var auth = useAuth();
  var [open, setOpen] = useState(false);
  var [loaded, setLoaded] = useState(false);
  var [busy, setBusy] = useState(false);
  var [query, setQuery] = useState('');
  var [employees, setEmployees] = useState([]);
  var [employeeId, setEmployeeId] = useState('');
  var [convs, setConvs] = useState([]);
  var [detail, setDetail] = useState(null);
  var [exporting, setExporting] = useState(false);
  var [visibleTurns, setVisibleTurns] = useState(30);

  if (!auth.isFullAccess || !auth.isFullAccess()) return null;

  function loadList(q, emp) {
    setBusy(true);
    setDetail(null);
    assistantAPI.adminChatlogs(q || '', emp || '').then(function ok(r) {
      setConvs(r.data.conversations || []);
      setBusy(false);
    }).catch(function fail() { setBusy(false); });
  }

  function handleOpen() {
    var next = !open;
    setOpen(next);
    if (next && !loaded) {
      setLoaded(true);
      assistantAPI.adminChatlogEmployees().then(function ok(r) {
        setEmployees(r.data.employees || []);
      }).catch(function fail() {});
      loadList('', '');
    }
  }

  function openDetail(id) {
    setBusy(true);
    assistantAPI.adminChatlogDetail(id).then(function ok(r) {
      setDetail(r.data);
      setVisibleTurns(30);
      setBusy(false);
    }).catch(function fail() { setBusy(false); });
  }

  function doExport(fmt) {
    setExporting(true);
    assistantAPI.adminChatlogExport(employeeId, fmt).then(function ok(r) {
      var blob;
      if (fmt === 'csv') {
        blob = r.data;
      } else {
        blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      }
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'ki-chatlogs-' + (employeeId ? 'mitarbeiter' : 'alle') + '.' + fmt;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExporting(false);
    }).catch(function fail() { setExporting(false); });
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="chatlog-section">
      <button type="button" onClick={handleOpen}
        data-testid="chatlog-toggle"
        className="w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-2 font-semibold text-slate-800">
          <MessageCircle className="h-4 w-4 text-blue-600" /> KI-Chatlogs (Mitarbeiter-Auswertung)
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>

      {open ? (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-slate-500">
            Nur für Geschäftsführung &amp; Admin. Zeigt, wie das KI-Tool genutzt wird — inkl. Bewertungen
            (Daumen hoch/runter), Kopien und Klicks. Daraus lässt sich ablesen, was gut funktioniert.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="h-4 w-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={query}
                onChange={function onQ(e) { setQuery(e.target.value); }}
                onKeyDown={function onK(e) { if (e.key === 'Enter') loadList(query, employeeId); }}
                placeholder="In allen Chats suchen …"
                data-testid="chatlog-search"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-lg" />
            </div>
            <select value={employeeId}
              onChange={function onEmp(e) { setEmployeeId(e.target.value); loadList(query, e.target.value); }}
              data-testid="chatlog-employee-filter"
              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="">Alle Mitarbeiter</option>
              {employees.map(function mapEmp(em) {
                return (
                  <option key={em.id} value={em.id}>
                    {em.name}{em.message_count != null ? ' (' + em.message_count + ')' : ''}
                  </option>
                );
              })}
            </select>
            <button type="button" onClick={function onSearch() { loadList(query, employeeId); }}
              data-testid="chatlog-search-btn"
              className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              Suchen
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Export {employeeId ? '(gewählter Mitarbeiter)' : '(alle)'}:</span>
            <button type="button" disabled={exporting} onClick={function onCsv() { doExport('csv'); }}
              data-testid="chatlog-export-csv"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button type="button" disabled={exporting} onClick={function onJson() { doExport('json'); }}
              data-testid="chatlog-export-json"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> JSON
            </button>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : null}
          </div>

          {busy ? (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Laden …
            </div>
          ) : null}

          {detail ? (
            <div className="border border-slate-200 rounded-lg p-3" data-testid="chatlog-detail">
              <button type="button" onClick={function onBack() { setDetail(null); }}
                data-testid="chatlog-back"
                className="text-xs text-blue-600 hover:underline mb-2">&#8592; zurück zur Liste</button>
              <div className="text-sm font-semibold text-slate-800">
                {detail.employee_name} — {detail.title || 'Ohne Titel'}
              </div>
              <div className="mt-2">
                {(detail.turns || []).slice(0, visibleTurns).map(function mapTurn(t, i) {
                  return <TurnRow key={t.id || i} t={t} />;
                })}
                {detail.turns && detail.turns.length > visibleTurns && (
                  <button type="button"
                    onClick={function loadMore() { setVisibleTurns(function add(v) { return v + 30; }); }}
                    className="mt-2 w-full text-sm py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
                    Mehr laden ({detail.turns.length - visibleTurns} weitere)
                  </button>
                )}              </div>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[420px] overflow-auto"
              data-testid="chatlog-list">
              {convs.length === 0 && !busy ? (
                <div className="text-sm text-slate-400 p-4">Keine Chats gefunden.</div>
              ) : null}
              {convs.map(function mapConv(c) {
                return (
                  <button key={c.id} type="button" onClick={function onOpen() { openDetail(c.id); }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between">
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-slate-800 truncate">{c.employee_name}</span>
                      <span className="block text-xs text-slate-500 truncate">{c.title || 'Ohne Titel'}</span>
                    </span>
                    <span className="text-[11px] text-slate-400 whitespace-nowrap ml-2">
                      {c.turn_count} Nachr. · {(c.updated_at || '').slice(0, 10)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
